import { useCallback, useState } from 'react';

export type UseRetryingBatchIdsResult = {
  /** Ids currently being retried by any in-flight call to {@link track}. */
  retryingBatchIds: string[];
  /**
   * Runs `run`, marking `ids` as retrying for its duration. Merges into any
   * already in-flight ids (rather than replacing them) and removes only its
   * own ids when `run` settles, so concurrent retries — e.g. two different
   * presentation groups' cards, a custom integration's own retry running
   * alongside the peripheral one, or two overlapping calls that happen to
   * share a batch id — can't clobber each other's pending state.
   */
  track: <T>(ids: string[], run: () => Promise<T>) => Promise<T>;
};

/**
 * Shared bookkeeping for "which batches are currently being retried", so any
 * retry mechanism (the built-in peripheral retry-activate flow, or a custom
 * integration's presentation-group handler) gets the same
 * per-group disabled-button / blocked-close behavior for free.
 */
export const useRetryingBatchIds = (): UseRetryingBatchIdsResult => {
  // Reference-counted per id, not a flat set: two overlapping `track` calls
  // can share an id (same batch retried twice before the UI disables the
  // button), and removing by value on the first call to settle would
  // re-enable/unblock the card while the second call is still pending.
  const [refCounts, setRefCounts] = useState<Record<string, number>>({});

  const track = useCallback(
    async <T>(ids: string[], run: () => Promise<T>): Promise<T> => {
      setRefCounts((prev) => {
        const next = { ...prev };
        for (const id of ids) {
          next[id] = (next[id] ?? 0) + 1;
        }
        return next;
      });
      try {
        return await run();
      } finally {
        setRefCounts((prev) => {
          const next = { ...prev };
          for (const id of ids) {
            const count = (next[id] ?? 0) - 1;
            if (count > 0) {
              next[id] = count;
            } else {
              delete next[id];
            }
          }
          return next;
        });
      }
    },
    []
  );

  return { retryingBatchIds: Object.keys(refCounts), track };
};
